import React, { Fragment, useCallback } from 'react';
import styled from 'styled-components';

import {
  calculatePagePercentageAdjustments,
  getPercentageAdjustedValue,
  reversePercentageAdjustedValue,
  getPageFieldPositions,
} from '../../lib/documentHelper';

import FieldModes from '../../constants/FieldModes';
import FormStylesOptions from '../../constants/FormStylesOptions';

import GridLayout from './GridLayout';
import FieldCreationDropLayer from './FieldCreationDropLayer';

const StyledWrapper = styled.div`
  display: flex;
  overflow: hidden;
  width: ${(props) => props.width}px;

  /**
   * IMPORTANT NOTE: We need this to have an actual height value so 
   * that as the height of the parent element is reduced a scrollbar 
   * appears and the page isn't cut off. Do not make this value "auto"
   * or else we will lose the scrollbar functionality.
   */
  height: ${(props) => props.height}px;
`;

const StyledBorderWrapper = styled.div`
  position: relative;
  border: ${(props) => props.border};
  padding: ${(props) => props.padding || 0}px;
  margin: ${(props) => props.margin || 0}px;
  display: flex;
  flex: 1;
  justify-content: center;
`;

const StyledPageImage = styled.img`
  top: auto;
  right: auto;
  left: auto;
  bottom: auto;
  z-index: 2;
  position: absolute;
  box-sizing: border-box;
  width: ${(props) => props.width}px;

  /*
   * IMPORTANT NOTE: height and max-height should not be changed without
   * reviewing PDF Form specs to understand the implications of the change.
   */
  height: ${(props) => props.height}px;
  max-height: ${(props) => props.maxHeight}px;
`;

const StyledPageFields = styled.div`
  display: flex;
  top: auto;
  right: auto;
  left: auto;
  bottom: auto;
  z-index: 3;
  position: absolute;
  width: ${(props) => props.width}px;

  /*
   * IMPORTANT NOTE: This element determines the available field creation
   * dropzone dimensions of the form. This must take up the max available 
   * height in order to ensure the field creation dropzone covers the full 
   * image area and allows field creation anywhere on the background image.
   */
  height: ${(props) => props.height}px;

`;

const PDFPage = (props) => {

  const {

    id, //PageId
    mode,

    //Container properties
    containerWidth = 816,
    containerHeight = 1056,

    /**
     * When true the page scales to fill the container width (preserving aspect ratio)
     * and is allowed to overflow vertically with scroll. Used for landscape PDF pages
     * so the background fills the available workspace width instead of being shrunk to
     * fit the limited viewport height. See File.js (isLandscapeActivePage).
     */
    fitToWidth = false,

    //Page Properties
    width = 816,
    height = 1056,
    rowHeight,
    presentation,
    backgroundImage,
    backgroundSize,
    margin,
    padding,
    borderWidth,
    borderColor,
    borderStyle,

    fields,

    mockTestDndMonitor,
    onFieldCreate,
    onFieldLayoutsChange

  } = props;

  /**
   * Handle container max width and height adjustments
   *
   * The page margin, padding, and border should be applied to the container and
   * reduce the max dimensions of the container element.
   *
   * Why is this? The container element determines the max dimensions that the
   * fields and PDF background can be within. The border, padding, and margin of
   * the page itself reduce that max dimension limit. Therefore we apply those
   * page properties to the container since that is what enforces those dimensions
   * for the page fields and PDF background.
   *
   * For instance if the container max width is 816px and the page has padding of
   * 4px (x2 for both sides) then the max width of the container should be reduced
   * to 804px. Then we can scale the PDF background and fields to fit within that
   * new 804px width limit.
   */
  const adjustedContainerWidth = getAdjustedDimension(containerWidth, padding, margin, borderWidth);
  const adjustedContainerHeight = getAdjustedDimension(containerHeight, padding, margin, borderWidth);

  const percentageDifference = calculatePagePercentageAdjustments(height, width, adjustedContainerHeight, adjustedContainerWidth, fitToWidth);

  const adjustedPageWidth = getPercentageAdjustedValue(width, percentageDifference);
  const adjustedPageHeight = getPercentageAdjustedValue(height, percentageDifference);

  /**
   * We join width and color to a single border style because chrome displays
   * the border as a two tone color if you use border-color and border-width
   * indvidually.
   */
  const pageBorderWidth = borderWidth !== undefined ? `${borderWidth}px` : '0px';
  const pageBorderColor = borderColor || 'transparent';
  const pageBorderStyle = borderStyle || 'solid';
  const pageBorder = `${pageBorderWidth} ${pageBorderStyle} ${pageBorderColor}`;

  /**
   * Start Field Helpers
   */

  const getFields = useCallback(() => {

    const adjustedFields = fields.map((field) => {
      return {
        ...field,
        x: getPercentageAdjustedValue(field.x, percentageDifference),
        y: getPercentageAdjustedValue(field.y, percentageDifference),
        width: getPercentageAdjustedValue(field.width, percentageDifference),
        height: getPercentageAdjustedValue(field.height, percentageDifference)
      };
    });

    return getPageFieldPositions({
      fields: adjustedFields,
      mode,
      presentation,
    });

  }, [fields, percentageDifference, mode, presentation]);

  const handleFieldCreation = useCallback((pageId, field) => {

    const newField = {
      ...field,
      x: reversePercentageAdjustedValue(field.x, percentageDifference),
      y: reversePercentageAdjustedValue(field.y, percentageDifference),
      width: reversePercentageAdjustedValue(field.width, percentageDifference),
      height: reversePercentageAdjustedValue(field.height, percentageDifference)
    };

    if (onFieldCreate) onFieldCreate(pageId, newField);

  }, [
    percentageDifference, 

    /**
     * VERY IMPORTANT: We need to keep fields to the dependency array to ensure 
     * the handleFieldCreation function is re-created when the fields change. By 
     * not having this it created the bug in Notion ticket NO-1512
     */
    fields
  ]);

  const handleFieldLayoutsChange = useCallback((pageId, layouts) => {

    const adjustedLayouts = layouts.map((layout) => {

      const adjustedLayout = {
        ...layout,
        x: reversePercentageAdjustedValue(layout.x, percentageDifference),
        y: reversePercentageAdjustedValue(layout.y, percentageDifference),
        width: reversePercentageAdjustedValue(layout.width, percentageDifference),
        height: reversePercentageAdjustedValue(layout.height, percentageDifference)
      };

      /**
       * Prevents fields from being dragged below the PDF Form bottom boundary.
       *
       * Works 80% of the time but due to an issue with react-grid-layout
       * some scenarios don't reflect the field adjustment until after the
       * next re-render cycle of the grid.
       */
      const totalFieldYRequirements = adjustedLayout.y + adjustedLayout.height;
      if (totalFieldYRequirements > height) {
        adjustedLayout.y = height - (adjustedLayout.height + 8);
      }

      return adjustedLayout;

    });

    if (onFieldLayoutsChange) onFieldLayoutsChange(pageId, adjustedLayouts);

  }, [height, percentageDifference]);

  const FieldCreationDropLayerElement = mode === FieldModes.edit ? FieldCreationDropLayer : Fragment;

  const dropElementProps = {};

  if (mode === FieldModes.edit) {

    dropElementProps.pageId = id;
    dropElementProps.pageWidth = adjustedPageWidth;
    dropElementProps.pageColumns = adjustedPageWidth;
    dropElementProps.pageRowHeight = rowHeight;
    dropElementProps.onDrop = handleFieldCreation;
    dropElementProps.mockTestDndMonitor = mockTestDndMonitor;

  }

  return (
    <StyledWrapper
      {...{
        width: containerWidth,

        /**
         * For width-fill (landscape) pages the scaled page height can exceed the
         * container height. Size the wrapper to the actual page height so the parent
         * scroll container (StyledMiddlePanelBody in File.js, overflow-y: auto) can
         * scroll instead of clipping the page via the fixed-height + overflow:hidden.
         */
        height: fitToWidth ? adjustedPageHeight : containerHeight,
      }}
    >
      <StyledBorderWrapper
        {...{
          border: pageBorder,
          margin,
          padding
        }}
      >
        <StyledPageImage
          {...{
            width: adjustedPageWidth,
            height: backgroundSize === FormStylesOptions.backgroundSizeStretch ? adjustedPageHeight : 'auto',
            maxHeight: adjustedPageHeight,
          }}
          src={backgroundImage}
        />
        <StyledPageFields
          {...{
            width: adjustedPageWidth,
            height: adjustedPageHeight,
          }}
        >
          <FieldCreationDropLayerElement
            {...dropElementProps}
          >
            <GridLayout
              {...props}
              fields={getFields()}
              width={adjustedPageWidth}
              cols={adjustedPageWidth}
              onFieldLayoutsChange={handleFieldLayoutsChange}
            />
          </FieldCreationDropLayerElement>
        </StyledPageFields>
      </StyledBorderWrapper>
    </StyledWrapper>
  );

};

/**
 * What is 2 used for?
 *
 * We do (* 2) to represent the number of sides of the page to take into account.
 *
 * For instance if you want to adjust the height of the page by subtracting the margin
 * from top and bottom of the page then you would use a multiple of 2.
 */
const getAdjustedDimension = (value, padding, margin, borderWidth) => {

  let pageValue = value;
  if (padding) pageValue -= (padding * 2);
  if (borderWidth) pageValue -= (borderWidth * 2);
  if (margin) pageValue -= (margin * 2);

  return pageValue;

};

export default PDFPage;
